/**
 * The test-result code list, in one place so the Edit and the Read/Print stacks cannot disagree.
 *
 * Test results are NOT a customer-managed vocabulary — there is no TestResult table — so they are a
 * static option array taking the Picker broker's generic (non-entity) select path, rather than an entry
 * in `_VocabularySelectorMap`.
 *
 * Both stacks must stamp it: `SampleTestAcceptance` renders the field in Edit, but `PickerDisplay`
 * renders it in Read and Print, and that provider never runs the acceptance input's initialize hooks.
 * Without the options on the descriptor, the display has nothing to map the stored code through and
 * prints the raw `PEND` instead of `Pending`.
 */

/** @type {Array<{id: string, text: string}>} */
const DEFAULT_TEST_RESULT_OPTIONS =
[
	{ 'id': 'N/A', 'text': 'N/A' },
	{ 'id': 'PROG', 'text': 'In Progress' },
	{ 'id': 'PEND', 'text': 'Pending' },
	{ 'id': 'FAIL', 'text': 'Failed' },
	{ 'id': 'FAIC', 'text': 'Failed with Comments' },
	{ 'id': 'PASS', 'text': 'Passed' },
	{ 'id': 'PASC', 'text': 'Passed with Comments' },
	{ 'id': 'SQTY', 'text': 'Small Quantity' },
	{ 'id': 'VOID', 'text': 'Voided' },
];

/** InputTypes whose options this module owns. */
const TEST_RESULT_INPUT_TYPES = [ 'SampleTestAcceptance' ];

/**
 * Stamp the default result codes onto an acceptance descriptor. Never overwrites a schema-supplied
 * `SelectOptions`, and leaves every other InputType untouched.
 *
 * @param {Object} pInput - The form input descriptor.
 * @param {boolean} [pForce] - Stamp regardless of InputType (the Edit provider knows it owns the field).
 * @return {boolean} - True when this descriptor carries the test-result option set.
 */
function applyTestResultOptions(pInput, pForce)
{
	if (!pInput)
	{
		return false;
	}
	if (!pInput.PictForm)
	{
		pInput.PictForm = {};
	}
	if (!pForce && TEST_RESULT_INPUT_TYPES.indexOf(pInput.PictForm.InputType) < 0)
	{
		return false;
	}
	if (!pInput.PictForm.SelectOptions || !Array.isArray(pInput.PictForm.SelectOptions))
	{
		pInput.PictForm.SelectOptions = DEFAULT_TEST_RESULT_OPTIONS;
	}
	return true;
}

/**
 * The display text for a stored test-result code.
 *
 * @param {string} pCode - The stored code, such as `PASC`.
 * @return {string} - Its text, or the value itself when it isn't a known code.
 */
function getTestResultLabel(pCode)
{
	const tmpOption = DEFAULT_TEST_RESULT_OPTIONS.find((pOption) => pOption.id === pCode);
	return tmpOption ? tmpOption.text : pCode;
}

module.exports = { DEFAULT_TEST_RESULT_OPTIONS, TEST_RESULT_INPUT_TYPES, applyTestResultOptions, getTestResultLabel };
